import { useState } from 'react';
import type { MemoryRead } from '../../../api/types';
import EmotionChart from './EmotionChart';

interface FirstMemoryScreenProps {
  memory: MemoryRead | null;
  status: 'idle' | 'uploading' | 'completed' | 'failed';
  error?: string | null;
  durationSeconds?: number;
  onRetry: () => void;
  onNext: () => void;
}

const emotionData = (memory: MemoryRead) => [
  { label: 'Радость', value: memory.joy_score ?? 0, color: '#3ECFA0', gradient: 'linear-gradient(90deg, #3ECFA0, #2BA87F)' },
  { label: 'Печаль', value: memory.sadness_score ?? 0, color: '#4A90E2', gradient: 'linear-gradient(90deg, #4A90E2, #357ABD)' },
  { label: 'Страх', value: memory.fear_score ?? 0, color: '#8B7AB8', gradient: 'linear-gradient(90deg, #8B7AB8, #6B5A98)' },
  { label: 'Гнев', value: memory.anger_score ?? 0, color: '#FF6B6B', gradient: 'linear-gradient(90deg, #FF6B6B, #EE5A52)' },
];

const formatDate = (value: string) => new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
}).format(new Date(`${value}T00:00:00`));

const formatTime = (value: string) => new Intl.DateTimeFormat('ru-RU', {
  hour: '2-digit',
  minute: '2-digit',
}).format(new Date(value));

const formatDuration = (seconds?: number) => {
  if (!seconds) return null;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (minutes === 0) return `${rest} сек`;
  return `${minutes} мин ${rest} сек`;
};

export default function FirstMemoryScreen({
  memory,
  status,
  error,
  durationSeconds,
  onRetry,
  onNext,
}: FirstMemoryScreenProps) {
  const [expanded, setExpanded] = useState(false);
  const isReady = status === 'completed' && memory;
  const duration = formatDuration(durationSeconds);
  const storyText = memory?.story_text || memory?.transcript || 'Текст воспоминания пока не найден.';
  const meta = memory
    ? [formatDate(memory.event_date), formatTime(memory.recorded_at), duration].filter(Boolean).join(' · ')
    : null;
  const canContinue = Boolean(isReady);

  return (
    <div
      className="flex flex-col w-full h-screen overflow-y-auto"
      style={{
        background: 'linear-gradient(170deg, #1A1B2E 0%, #2D1B3D 60%, #1F1A35 100%)',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* Header */}
      <div className="px-7 pt-14 pb-4">
        <div className="flex items-center gap-3 mb-3">
          <div
            style={{
              width: '40px', height: '40px', borderRadius: '12px', overflow: 'hidden', flexShrink: 0,
              boxShadow: '0 4px 16px rgba(62,207,160,0.3)',
            }}
          >
            <img
              src="/images/onboarding/omut-logo.png"
              alt="logo"
              className="w-full h-full object-cover"
            />
          </div>
          <p style={{ fontSize: '13px', color: '#6B7A94' }}>Омут Памяти</p>
        </div>
        <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#F5F5F7', lineHeight: 1.4 }}>
          Ваше воспоминание уже загружено в Омут Памяти.
        </h1>
      </div>

      {/* Memory card */}
      <div className="px-5 mb-4">
        <div
          className="rounded-3xl overflow-hidden"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <div style={{ padding: '18px 18px 14px' }}>
            {isReady ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3ECFA0' }} />
                      <span style={{ fontSize: '17px', fontWeight: 600, color: '#F5F5F7' }}>{memory.title || 'Первое воспоминание'}</span>
                    </div>
                    <p style={{ fontSize: '13px', color: '#6B7A94', paddingLeft: '16px' }}>{meta}</p>
                  </div>
                  <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: 'rgba(62,207,160,0.12)' }}>
                    <i className="ri-mic-fill text-sm" style={{ color: '#3ECFA0' }} />
                  </div>
                </div>

                {expanded && (
                  <div
                    style={{
                      marginTop: '14px',
                      paddingTop: '14px',
                      borderTop: '1px solid rgba(255,255,255,0.07)',
                    }}
                  >
                    <p style={{ fontSize: '14px', color: '#A8B2C8', lineHeight: 1.75 }}>
                      {storyText}
                    </p>
                    {memory.emotion_rationale && (
                      <p style={{ fontSize: '13px', color: '#6B7A94', lineHeight: 1.6, marginTop: '12px' }}>
                        {memory.emotion_rationale}
                      </p>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div
                style={{
                  minHeight: '154px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  gap: '12px',
                }}
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full" style={{ background: status === 'failed' ? 'rgba(255,107,107,0.12)' : 'rgba(62,207,160,0.12)' }}>
                  <i
                    className={status === 'failed' ? 'ri-error-warning-line text-2xl' : 'ri-loader-4-line text-2xl'}
                    style={{ color: status === 'failed' ? '#FFB4B4' : '#3ECFA0' }}
                  />
                </div>
                <p style={{ fontSize: '16px', fontWeight: 600, color: '#F5F5F7' }}>
                  {status === 'failed' ? 'Не удалось получить метрики' : 'Анализируем запись'}
                </p>
                <p style={{ fontSize: '14px', color: '#A8B2C8', lineHeight: 1.6, maxWidth: '260px' }}>
                  {status === 'failed'
                    ? (error || 'Попробуйте записать первое воспоминание ещё раз.')
                    : 'Расшифровываем голос, превращаем его в историю и считаем эмоциональный профиль.'}
                </p>
                {status === 'failed' && (
                  <button
                    onClick={onRetry}
                    style={{
                      height: '40px',
                      padding: '0 18px',
                      borderRadius: '20px',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      color: '#F5F5F7',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Записать заново
                  </button>
                )}
              </div>
            )}
          </div>

          {isReady && (
            <button
              onClick={() => setExpanded(!expanded)}
              style={{
                width: '100%', padding: '12px 18px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                background: 'rgba(62,207,160,0.08)',
                border: 'none', borderTop: '1px solid rgba(255,255,255,0.06)',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: '14px', fontWeight: 500, color: '#3ECFA0' }}>
                {expanded ? 'Свернуть' : 'Развернуть'}
              </span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i
                  className={expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}
                  style={{ color: '#3ECFA0', fontSize: '16px' }}
                />
              </div>
            </button>
          )}
        </div>
      </div>

      <div className="px-5 mb-5">
        <p style={{ fontSize: '14px', fontWeight: 500, color: '#6B7A94', marginBottom: '10px', paddingLeft: '2px' }}>
          Эмоциональный профиль
        </p>
        {isReady ? (
          <EmotionChart compact data={emotionData(memory)} />
        ) : (
          <div
            className="rounded-3xl"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.08)',
              padding: '18px',
            }}
          >
            <p style={{ fontSize: '14px', color: '#A8B2C8', lineHeight: 1.6, textAlign: 'center' }}>
              Метрики появятся здесь сразу после ответа сервера.
            </p>
          </div>
        )}
      </div>

      <div
        className="px-7 mt-auto"
        style={{ paddingBottom: 'calc(32px + env(safe-area-inset-bottom))' }}
      >
        <button
          onClick={() => {
            if (canContinue) onNext();
          }}
          disabled={!canContinue}
          className="w-full active:scale-95 transition-transform duration-150"
          style={{
            height: '58px', borderRadius: '29px',
            background: canContinue
              ? 'linear-gradient(135deg, #3ECFA0, #2BA87F)'
              : 'rgba(255,255,255,0.1)',
            border: 'none', fontSize: '17px', fontWeight: 600, color: '#fff',
            cursor: canContinue ? 'pointer' : 'default',
            boxShadow: canContinue ? '0 8px 32px rgba(62,207,160,0.4)' : 'none',
            opacity: canContinue ? 1 : 0.58,
          }}
        >
          {canContinue ? 'Погрузиться в Омут Памяти' : 'Ждём метрики воспоминания'}
        </button>
      </div>
    </div>
  );
}
